import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { MyThunkDispatch, RootState } from 'store';

import { getCompanyBilling } from 'actions/companyBilling';
import { getCompanyConfiguration } from 'actions/companyConfiguration';
import { getAccount } from 'actions/user';
import AppBar from 'components/AppBar';
import FloatingButton from 'components/FloatingButton';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Search from 'components/Search';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';
import OptionsItems from 'components/UserList/Components/OptionsItems';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';

import BillingCompanyItem from './components/BillingCompanyItem';
import BranchCompanyEmptyList from './components/BranchCompanyEmptyList';
import { List, Container, FiltersRow, StatusFilterContainer } from './styles';

type StatusFilter = 'all' | 'active' | 'inactive';

interface Props {
  billingCompanyTarget?: string;
  billingCompanyList: GetAllBillingCompanyResponse[];
  isLoading: boolean;
  isSaving: boolean;
  ruleSelected?: GetAllBillingCompanyResponse;
  onCloseSelectionScreen?: () => void;
  onConfirmSelection?: (billingCompany: GetAllBillingCompanyResponse) => void;
  onToggle?: (billingCompany: GetAllBillingCompanyResponse) => void;
  onToggleStatus?: (billingCompany: GetAllBillingCompanyResponse) => void;
}

const BillingCompanySelectList = ({ billingCompanyList, isLoading, onToggleStatus, isSaving }: Props) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusValue, setStatusValue] = useState<StatusFilter>('active');
  const [statusText, setStatusText] = useState<string>('');
  const statusRef = useRef(null);
  const history = useHistory();
  const { t } = useStoreTranslation();
  const dispatch: MyThunkDispatch = useDispatch();
  const accountResult = useSelector((state: RootState) => state.user.accountResult);
  const [isImpersonateLoading, setIsImpersonateLoading] = useState(false);

  const handleImpersonate = async (billingCompany: GetAllBillingCompanyResponse, redirect: string) => {
    setIsImpersonateLoading(true);

    const user = await dispatch<any>(getAccount(billingCompany.id));
    await dispatch<any>(getCompanyBilling(billingCompany.id));
    await dispatch<any>(getCompanyConfiguration(billingCompany.id));

    if (user) {
      history.replace(redirect, { data: billingCompany });
    } else {
      history.replace('/corporate/login-not-authorized');
    }

    setIsImpersonateLoading(false);
  };
  const handleOnSearch = (value: string) => {
    setSearchQuery(value);
  };

  const statusList: { key: StatusFilter; value: string }[] = [
    { key: 'all', value: t('branchCompany.list.filterAll') },
    { key: 'active', value: t('branchCompany.list.filterActive') },
    { key: 'inactive', value: t('branchCompany.list.filterInactive') },
  ];

  const handleSelectStatus = (key: string) => {
    const status = statusList.find((s) => s.key === key);
    setStatusText(status?.value ?? '');
    setStatusValue((status?.key as StatusFilter) ?? 'all');
  };

  const filteredList = useMemo(
    () =>
      billingCompanyList.filter((billingCompany) => {
        const matchesStatus =
          statusValue === 'all' ||
          (statusValue === 'active' ? billingCompany.isActive !== false : billingCompany.isActive === false);
        const matchesSearch =
          !searchQuery || billingCompany.companyName.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1;
        return matchesStatus && matchesSearch;
      }),
    [billingCompanyList, statusValue, searchQuery],
  );

  const renderList = () => (
    <>
      <FiltersRow>
        <Search show={billingCompanyList.length >= 10} onSearch={handleOnSearch} />
        <StatusFilterContainer>
          <CustomSelect
            name="companyStatus"
            onChange={handleSelectStatus}
            label=""
            placeHolder={t('branchCompany.list.filterActive')}
            selectRef={statusRef}
            value={statusText || t('branchCompany.list.filterActive')}
          >
            {({ ...selectProps }: CustomSelectRenderProps) => (
              <OptionsItems {...selectProps} selectRef={statusRef} userStatus={statusList} />
            )}
          </CustomSelect>
        </StatusFilterContainer>
      </FiltersRow>

      <List>
        {filteredList.map((billingCompany) => (
          <BillingCompanyItem
            key={`user-${billingCompany.id}`}
            billingCompany={billingCompany}
            onSelectToImpersonate={() => handleImpersonate(billingCompany, '/corporate/booking')}
            onEdit={() => {
              handleImpersonate(billingCompany, '/corporate/configurations');
            }}
            onToggleStatus={onToggleStatus ? () => onToggleStatus(billingCompany) : undefined}
            loading={isSaving}
            currentCompanyId={accountResult.companyId}
          />
        ))}
      </List>
    </>
  );

  return (
    <>
      <Mobile>
        <AppBar goBack={() => history.push('/corporate/booking')}>
          <h5>{t('userMenu.billingCompanyTitle')}</h5>
        </AppBar>
      </Mobile>

      {isLoading || isImpersonateLoading ? (
        <Loader fullScreen />
      ) : (
        <>
          <Desktop>
            <ScreenHeader title={t('userMenu.billingCompanyTitle')} subtitle={t('userMenu.billingCompanyDetail')} />
          </Desktop>

          <Container hasList={billingCompanyList.length > 0}>
            {billingCompanyList && billingCompanyList.length > 0 ? renderList() : <BranchCompanyEmptyList />}
          </Container>
        </>
      )}
      {billingCompanyList.find((b) => b.isHolding && b.selected) !== undefined && (
        <FloatingButton zIndex={2} onClick={() => history.push('/corporate/billingCompany')} className="target" />
      )}
    </>
  );
};

export default BillingCompanySelectList;
